import React, { Component } from 'react';
import PublicReflectionProgress from './PublicReflectionProgress';
import { urlMap, getAuthHeaders } from '../../utils';
import { fetchSubscriptionStatus, openSubscriptionTabInWallet } from '../../utils/subscriptionAccess';

const authenticatedFetchReflection = (reflectionId) =>
  fetch(urlMap.writerSessionReflection(reflectionId), {
    headers: getAuthHeaders(),
    credentials: 'include',
  }).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });

function formatLongDuration(secondsInput) {
  const totalMinutes = Math.floor(Math.max(0, Number(secondsInput) || 0) / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
}

/**
 * Renders the progress tab content — project progress bar, writing stats, and
 * the reflection scatter chart.
 *
 * Two usage modes:
 *
 * 1. Authenticated (project page):
 *    Pass `projectId`. The component fetches data from the authenticated
 *    `/projects/:id/progress-feed` endpoint.
 *
 * 2. Pre-loaded (public feed):
 *    Pass `currentProject`, `totalWritingSeconds`, `reflectionAnswerSeconds`,
 *    `progress`, and `authorSlug` directly.
 */
export default class ProjectProgressView extends Component {
  state = {
    loading: false,
    error: '',
    fetched: null,
    hasSelectedReflection: false,
    exportAllLoading: false,
    exportAllError: '',
    isPro: false,
    proLoading: true,
  };

  isUnmounted = false;

  componentDidMount() {
    if (this.props.projectId) {
      this.loadSubscription();
    } else {
      this.setState({ proLoading: false, isPro: false });
    }
    if (this.props.projectId && !this.hasPreloadedData()) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.projectId && this.props.projectId !== prevProps.projectId) {
      this.loadSubscription();
    }
    if (this.props.projectId && this.props.projectId !== prevProps.projectId && !this.hasPreloadedData()) {
      this.fetchData();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  hasPreloadedData() {
    return this.props.currentProject !== undefined || this.props.progress !== undefined;
  }

  loadSubscription = async () => {
    const status = await fetchSubscriptionStatus();
    if (this.isUnmounted) return;
    this.setState({ isPro: !!status.isPro, proLoading: false });
  };

  fetchData = async () => {
    const { projectId } = this.props;
    if (!projectId) return;

    this.setState({ loading: true, error: '', fetched: null });
    try {
      const res = await fetch(urlMap.projectProgressFeed(projectId, 1, 25), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();

      if (this.isUnmounted) return;

      this.setState({
        loading: false,
        fetched: {
          authorSlug: body?.authorSlug || '',
          currentProject: body?.currentProject || null,
          progress: Array.isArray(body?.progress) ? body.progress : [],
          totalWritingSeconds: Number(body?.totalWritingSeconds) || 0,
          reflectionAnswerSeconds: Number(body?.reflectionAnswerSeconds) || 0,
        },
      });
    } catch {
      if (!this.isUnmounted) {
        this.setState({ loading: false, error: 'Could not load progress data.' });
      }
    }
  };

  resolveData() {
    const { fetched } = this.state;
    if (this.hasPreloadedData()) {
      return {
        authorSlug: this.props.authorSlug || '',
        currentProject: this.props.currentProject || null,
        progress: Array.isArray(this.props.progress) ? this.props.progress : [],
        totalWritingSeconds: Number(this.props.totalWritingSeconds) || 0,
        reflectionAnswerSeconds: Number(this.props.reflectionAnswerSeconds) || 0,
      };
    }
    return fetched;
  }

  handleSelectionChange = (hasSelection) => {
    if (this.state.hasSelectedReflection === hasSelection) return;
    this.setState({ hasSelectedReflection: hasSelection });
  };

  fetchReflectionForPoint = async (pointId, authorSlug) => {
    if (this.props.projectId) {
      return authenticatedFetchReflection(pointId);
    }
    if (!authorSlug) {
      throw new Error('Missing author slug.');
    }
    const response = await fetch(urlMap.publicAuthorReflection(authorSlug, pointId));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  handleExportAllReflections = async () => {
    if (!this.state.isPro) {
      openSubscriptionTabInWallet();
      return;
    }

    const data = this.resolveData();
    if (!data) return;

    const { progress, currentProject, authorSlug } = data;
    if (!Array.isArray(progress) || progress.length === 0) return;

    this.setState({ exportAllLoading: true, exportAllError: '' });
    try {
      const rows = await Promise.all(progress.map(async (point) => {
        let reflection = null;
        try {
          reflection = await this.fetchReflectionForPoint(point.id, authorSlug);
        } catch {
          reflection = null;
        }

        return {
          id: point.id,
          createdAt: point.created_at,
          difficultyScore: point.difficulty_score,
          reflection,
        };
      }));

      const payload = {
        projectId: currentProject?.id || null,
        projectTitle: currentProject?.title || 'project',
        exportedAt: new Date().toISOString(),
        reflectionCount: rows.length,
        reflections: rows,
      };

      const safeName = (currentProject?.title || 'project')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'project';
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}-reflections.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.setState({ exportAllLoading: false });
    } catch {
      this.setState({ exportAllLoading: false, exportAllError: 'Could not export reflections.' });
    }
  };

  handleReflectionProInfoClick = () => {
    this.setState({ exportAllError: 'Pro subscription required for reflection export.' });
  };

  render() {
    const {
      loading,
      error,
      hasSelectedReflection,
      exportAllLoading,
      exportAllError,
      isPro,
      proLoading,
    } = this.state;

    if (loading) {
      return (
        <div className="public-progress-view public-progress-view--loading">
          <p>Loading progress…</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="public-progress-view public-progress-view--error">
          <p>{error}</p>
        </div>
      );
    }

    const data = this.resolveData();
    if (!data) return null;

    const { authorSlug, currentProject, progress, reflectionAnswerSeconds } = data;
    const rowClassName = `public-progress-view__project-row${!hasSelectedReflection ? ' public-progress-view__project-row--compact' : ''}`;

    return (
      <div className="public-progress-view">
        {currentProject && (
          <div className="public-progress-view__project">
            <div className={rowClassName}>
              <dl className="public-progress-view__metadata">
                <div>
                  <dt>Reflections</dt>
                  <dd>{progress.length}</dd>
                </div>
                <div>
                  <dt>Reflection time</dt>
                  <dd>{formatLongDuration(reflectionAnswerSeconds)}</dd>
                </div>
              </dl>
              <div className="public-progress-view__export-actions">
                <button
                  type="button"
                  className="public-progress-view__export-btn"
                  onClick={this.handleExportAllReflections}
                  disabled={exportAllLoading || proLoading || progress.length === 0}
                  title="Export reflections"
                >
                  {!isPro && !exportAllLoading && (
                    <span className="public-progress-view__export-lock" aria-hidden="true">
                      <svg viewBox="0 0 20 20" width="12" height="12" fill="currentColor">
                        <path fillRule="evenodd" d="M10 1a4 4 0 0 0-4 4v2H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V5a4 4 0 0 0-4-4Zm2 6V5a2 2 0 1 0-4 0v2h4Z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                  <span>{exportAllLoading ? 'Exporting…' : 'Export reflections'}</span>
                </button>
                {!isPro && !exportAllLoading && (
                  <button
                    type="button"
                    className="public-progress-view__export-info"
                    title="Why Pro is required"
                    aria-label="Why Pro is required"
                    onClick={this.handleReflectionProInfoClick}
                  >
                    i
                  </button>
                )}
              </div>
            </div>
            {exportAllError ? <p className="public-progress-view__export-error">{exportAllError}</p> : null}
          </div>
        )}
        <PublicReflectionProgress
          authorSlug={authorSlug}
          points={progress}
          fetchReflection={this.props.projectId ? authenticatedFetchReflection : null}
          onSelectionChange={this.handleSelectionChange}
        />
      </div>
    );
  }
}
